import { Alert, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { DirectionReviewReason } from 'enums/prescription.enum';

/**
 * Says why the order is in Direction Review, in the reviewer's language. Reasons the reviewer can
 * settle on this screen read as information; the two that need someone else read as a warning.
 *
 * The sig is clinical text, so it is passed as a child and never assembled into markup.
 */
const buildReasonNotice = (reviewReason, currentSig) => {
  const sigName = (
    <Typography component='span' sx={{ fontWeight: 600 }}>
      {currentSig}
    </Typography>
  );

  switch (reviewReason) {
    case DirectionReviewReason.NOT_APPROVED_FOR_DRUG:
      return {
        severity: 'info',
        message: currentSig ? (
          <>This direction already means {sigName}. That sig is not yet approved for this drug.</>
        ) : (
          'This direction already means a sig that is not yet approved for this drug.'
        ),
      };
    case DirectionReviewReason.SIG_RETIRED:
      return {
        severity: 'info',
        message: currentSig ? (
          <>This direction used to mean {sigName}, which has been retired.</>
        ) : (
          'This direction used to mean a sig that has been retired.'
        ),
      };
    case DirectionReviewReason.NEW_DIRECTION:
      return { severity: 'info', message: 'This direction has not been seen before.' };
    case DirectionReviewReason.AMBIGUOUS_DIRECTION:
      return {
        severity: 'warning',
        message:
          'This direction currently has more than one meaning and needs an admin to settle it.',
      };
    case DirectionReviewReason.NO_CATALOG_ITEM:
      return {
        severity: 'warning',
        message: 'The product for this order could not be identified.',
      };
    default:
      return null;
  }
};

const DirectionReviewReasonBanner = ({ reviewReason, currentSig }) => {
  const notice = buildReasonNotice(reviewReason, currentSig);

  if (!notice) {
    return null;
  }

  return (
    <Alert severity={notice.severity} sx={{ mb: 2 }}>
      {notice.message}
    </Alert>
  );
};

DirectionReviewReasonBanner.propTypes = {
  reviewReason: PropTypes.string,
  currentSig: PropTypes.string,
};

DirectionReviewReasonBanner.defaultProps = {
  reviewReason: '',
  currentSig: '',
};

export default DirectionReviewReasonBanner;
